import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import type { FormInstance } from 'antd';
import {
  Alert,
  Avatar,
  Button,
  Card,
  Empty,
  Form,
  Input,
  Popconfirm,
  Skeleton,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { DeleteOutlined, DownloadOutlined, ImportOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import type { CreateStudentRequest, StudentListRow } from '@velora/contracts';
import { ApiError, productApi } from '../../../api';
import {
  CodeField,
  DateField,
  FieldDefaultsProvider,
  GenderField,
  GradeField,
  PhoneField,
  RelationField,
  SectionField,
  TextField,
  type FieldDefaults,
  type Gender,
} from '../../../ui/fields';
import { Field, FormModal, FormSection, V } from '../../../ui/forms';

/**
 * Admin surface — Screen 17. Student directory.
 * First vertical slice with real data: list, filter, create. Attendance / fees /
 * flags columns still show "—" until those modules ship — they read derived data
 * from tables that don't exist yet.
 */
export function StudentsListPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [grade, setGrade] = useState<string | undefined>();
  const [section, setSection] = useState<string | undefined>();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [addOpen, setAddOpen] = useState(false);

  const list = useQuery({
    queryKey: ['students', institution, { q, grade, section, page, pageSize }],
    queryFn: () =>
      productApi.listStudents(institution, {
        q: q || undefined,
        grade,
        section,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      }),
    enabled: !!institution,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['students', institution] });
  const del = useMutation({
    mutationFn: (adm: string) => productApi.deleteStudent(institution, adm),
    onSuccess: () => { void invalidate(); void message.success('Marked as left'); },
    onError: (e) => void message.error(e instanceof Error ? e.message : 'Failed'),
  });

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>Students</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
            {list.data
              ? `${list.data.total} on file · attendance and fees columns arrive with those modules`
              : 'Loading roster…'}
          </Typography.Text>
        </div>
        <Space size={8}>
          <Button icon={<ImportOutlined />} disabled title="Excel import ships with Setup step 1">Import from Excel</Button>
          <Button icon={<DownloadOutlined />} disabled title="Export ships later">Export</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>
            Add student
          </Button>
        </Space>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <Input
          size="small"
          placeholder="Name or admission no."
          prefix={<SearchOutlined style={{ color: '#8a94a6' }} />}
          value={q}
          onChange={(e) => { setQ(e.target.value); setPage(1); }}
          allowClear
          style={{ maxWidth: 280 }}
        />
        <Input
          size="small"
          placeholder="Grade"
          value={grade ?? ''}
          onChange={(e) => { setGrade(e.target.value || undefined); setPage(1); }}
          allowClear
          style={{ width: 100 }}
        />
        <Input
          size="small"
          placeholder="Section"
          value={section ?? ''}
          onChange={(e) => { setSection(e.target.value || undefined); setPage(1); }}
          allowClear
          style={{ width: 100 }}
        />
      </div>

      <Card
        size="small"
        title={
          <span>
            {list.data ? `${list.data.total} students` : 'Students'}
            {(grade || section) && (
              <span style={{ color: '#8a94a6', fontWeight: 400 }}>
                {' · '}
                <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>
                  {[grade && `grade ${grade}`, section && `section ${section}`].filter(Boolean).join(' · ')}
                </span>
              </span>
            )}
          </span>
        }
        extra={
          <Typography.Text type="secondary" style={{ fontSize: 11.5 }}>
            Whole row opens the record
          </Typography.Text>
        }
      >
        {list.isLoading && <Skeleton active paragraph={{ rows: 6 }} />}
        {list.isError && (
          <Alert
            type="error"
            showIcon
            message="Could not load students"
            description={list.error instanceof Error ? list.error.message : String(list.error)}
          />
        )}
        {list.data && list.data.rows.length === 0 && (
          <Empty
            description={
              <span>
                No students yet.
                {' '}
                {q || grade || section
                  ? 'Try clearing filters, or '
                  : 'Add one below, or '}
                <a onClick={() => setAddOpen(true)}>add a student</a>.
              </span>
            }
            style={{ padding: '24px 0' }}
          />
        )}
        {list.data && list.data.rows.length > 0 && (
          <Table<StudentListRow>
            size="small"
            dataSource={list.data.rows}
            rowKey="id"
            pagination={{
              current: page,
              pageSize,
              total: list.data.total,
              showSizeChanger: true,
              pageSizeOptions: ['25', '50', '100', '200'],
              showTotal: (total, range) => `${range[0]}–${range[1]} of ${total}`,
              onChange: (nextPage, nextSize) => {
                setPage(nextPage);
                if (nextSize !== pageSize) {
                  setPageSize(nextSize);
                  setPage(1);
                }
              },
            }}
            onRow={(record) => ({
              onClick: () => navigate(`/i/${institution}/admin/students/${record.admission_number}`),
              style: { cursor: 'pointer' },
            })}
            columns={[
              {
                key: 'photo',
                width: 40,
                render: (_, r) => (
                  <Avatar
                    size={30}
                    style={{ background: `hsl(${(r.name.length * 37) % 360}, 40%, 60%)`, fontSize: 11 }}
                  >
                    {r.name.split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase()}
                  </Avatar>
                ),
              },
              {
                title: 'Name',
                dataIndex: 'name',
                key: 'name',
                render: (v: string) => <span style={{ fontWeight: 500, color: '#14213d' }}>{v}</span>,
              },
              {
                title: 'Adm. no.',
                dataIndex: 'admission_number',
                key: 'admission_number',
                width: 130,
                render: (v: string) => (
                  <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{v}</span>
                ),
              },
              {
                title: 'Grade',
                key: 'grade',
                width: 110,
                render: (_, r) => (
                  <span style={{ fontSize: 12 }}>
                    {r.grade}
                    {r.section ? <span style={{ color: '#8a94a6' }}> · {r.section}</span> : null}
                  </span>
                ),
              },
              {
                title: 'Primary guardian',
                key: 'guardian',
                render: (_, r) =>
                  r.primary_guardian ? (
                    <div style={{ fontSize: 12 }}>
                      {r.primary_guardian.name}{' '}
                      <span style={{ color: '#8a94a6' }}>· {r.primary_guardian.relation}</span>
                      <div style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 11.5, color: '#5b6478' }}>
                        {r.primary_guardian.phone}
                      </div>
                    </div>
                  ) : (
                    <span style={{ color: '#8a94a6', fontSize: 12 }}>—</span>
                  ),
              },
              {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                width: 110,
                render: (v: StudentListRow['status']) => (
                  <Tag color={v === 'enrolled' ? 'success' : v === 'graduated' ? 'processing' : 'default'}>
                    {v}
                  </Tag>
                ),
              },
              {
                title: 'Attendance',
                key: 'attendance',
                width: 100,
                render: () => (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>—</Typography.Text>
                ),
              },
              {
                title: 'Fees',
                key: 'fees',
                width: 100,
                render: () => (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>—</Typography.Text>
                ),
              },
              {
                key: 'action',
                width: 40,
                align: 'right',
                render: (_, r) => (
                  r.status === 'enrolled' ? (
                    <Popconfirm
                      title="Mark as left?"
                      description="D-06: we deactivate, not delete."
                      onConfirm={() => del.mutate(r.admission_number)}
                      okText="Mark left"
                      okButtonProps={{ danger: true }}
                    >
                      <Button
                        size="small"
                        type="text"
                        icon={<DeleteOutlined />}
                        danger
                        onClick={(e) => e.stopPropagation()}
                      />
                    </Popconfirm>
                  ) : null
                ),
              },
            ]}
          />
        )}
      </Card>

      <AddStudentModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        institution={institution}
        onCreated={() => {
          void invalidate();
          setAddOpen(false);
          void message.success('Student added');
        }}
      />
    </Space>
  );
}

type StudentFormValues = {
  admission_number: string;
  name: string;
  gender: Gender;
  dob: string;
  grade: string;
  section?: string;
  guardian_name: string;
  guardian_phone: string;
  guardian_relation: 'father' | 'mother' | 'guardian';
};

/** Minimum viable create form. Excel import ships alongside for bulk. */
function AddStudentModal({
  open,
  onClose,
  institution,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  institution: string;
  onCreated: () => void;
}) {
  const structure = useQuery({
    queryKey: ['structure', institution],
    queryFn: () => productApi.getStructure(institution),
    enabled: !!institution && open,
  });
  const grades = structure.data?.grades ?? [];

  const fieldDefaults = useMemo<FieldDefaults>(
    () => ({
      grade: { options: grades.map((g) => ({ value: g.name, label: g.name })) },
      section: {
        optionsFor: (g) => {
          const match = grades.find((row) => row.name === g);
          return (match?.sections ?? []).map((s) => ({ value: s.name, label: s.name }));
        },
      },
      phone: { defaultCountry: '+91' },
    }),
    [grades],
  );

  return (
    <FormModal<StudentFormValues>
      open={open}
      onClose={onClose}
      title="Add student"
      submitText="Add student"
      width={720}
      initial={{ gender: 'male', guardian_relation: 'father' }}
      mutation={(v) =>
        productApi.createStudent(institution, {
          admission_number: v.admission_number,
          name: v.name,
          gender: v.gender,
          dob: v.dob,
          grade: v.grade,
          section: v.section ?? '',
          guardians: [
            {
              name: v.guardian_name,
              phone: v.guardian_phone,
              relation: v.guardian_relation,
              is_primary: true,
            },
          ],
        } satisfies CreateStudentRequest)
      }
      onSuccess={onCreated}
      mapServerError={(err) => {
        if (err instanceof ApiError && err.status === 409) {
          const body = err.body as { reason?: string } | null;
          if (body?.reason === 'duplicate_admission_number') {
            return [{ name: 'admission_number', errors: ['This admission number is already in use'] }];
          }
        }
        return [];
      }}
    >
      {(vform) => (
        <FieldDefaultsProvider value={fieldDefaults}>
          <StudentFormBody form={vform.form} />
        </FieldDefaultsProvider>
      )}
    </FormModal>
  );
}

function StudentFormBody({ form }: { form: FormInstance<StudentFormValues> }) {
  const gradeWatch = Form.useWatch('grade', form);
  return (
    <>
      <FormSection cols={2}>
        <Field name="admission_number" label="Admission no." required>
          <CodeField placeholder="2025-0134" autoFocus />
        </Field>
        <Field name="name" label="Full name" required>
          <TextField placeholder="Aarav Kulkarni" />
        </Field>
        <Field name="gender" label="Gender" required>
          <GenderField />
        </Field>
        <Field name="dob" label="Date of birth" required>
          <DateField variant="dob" />
        </Field>
        <Field name="grade" label="Grade" required>
          <GradeField />
        </Field>
        <Field name="section" label="Section" optional dependencies={['grade']}>
          <SectionField grade={gradeWatch} />
        </Field>
      </FormSection>

      <FormSection title="Primary guardian" cols={3}>
        <Field name="guardian_name" label="Name" required>
          <TextField placeholder="S. Kulkarni" />
        </Field>
        <Field name="guardian_phone" label="Phone" required rules={[V.phone()]}>
          <PhoneField />
        </Field>
        <Field name="guardian_relation" label="Relation" required>
          <RelationField />
        </Field>
      </FormSection>
    </>
  );
}
